import { Button, makeStyles, mergeClasses, Tooltip, tokens } from '@fluentui/react-components'
import { CheckmarkRegular } from '@fluentui/react-icons'
import { forwardRef, type ReactNode } from 'react'

import type { WorkspaceItem } from '../../../../shared/models/WorkspaceItem'

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), min(100%, 230px)))',
    gap: tokens.spacingHorizontalM,
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  item: {
    width: '95%',
    aspectRatio: '1/1',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    padding: tokens.spacingHorizontalXS,
    borderRadius: tokens.borderRadius2XLarge,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: 'white' ,
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
    ':focus-within': { backgroundColor: tokens.colorSubtleBackgroundHover },
  },
  selected: {
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': { backgroundColor: tokens.colorBrandBackground2Hover },
    ':focus-within': { backgroundColor: tokens.colorBrandBackground2Hover },
  },
  open: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    width: '100%',
    minWidth: 0,
    padding: tokens.spacingHorizontalS,
    fontWeight: tokens.fontWeightRegular,
    ':hover': { backgroundColor: 'transparent' },
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100px',
    height: '100px',
    fontSize: '56px',
    color: tokens.colorBrandForeground1,
  },
  name: {
    width: '100%',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    textAlign: 'center',
  },
  indicator: { color: tokens.colorBrandForeground1, alignSelf: 'center' },
})

export function WorkspaceItemsGrid({ children }: { children: ReactNode }) {
  const styles = useStyles()
  return <ul className={styles.grid} aria-label="Workspace items">{children}</ul>
}

export const WorkspaceItemsGridTile = forwardRef<HTMLLIElement, {
  item: WorkspaceItem
  icon: ReactNode
  tooltip?: ReactNode
  selected?: boolean
  className?: string
  children?: ReactNode
  onOpen: (item: WorkspaceItem) => Promise<void>
}>(function WorkspaceItemsGridTile({ item, icon, tooltip, selected, className, children, onOpen }, ref) {
  const styles = useStyles()

  return (
    <li ref={ref} className={mergeClasses(styles.item, selected && styles.selected, className)}>
      {children}
      <Tooltip content={item.name} relationship="description">
        <Button
          appearance="subtle"
          className={styles.open}
          aria-label={`${selected === undefined ? 'Open' : 'Select'} ${item.name}`}
          aria-pressed={selected}
          onClick={() => void onOpen(item)}
        >
          <span className={styles.icon} aria-hidden="true">{icon}</span>
          <span className={styles.name}>{item.name}</span>
          {selected && <CheckmarkRegular aria-hidden="true" className={styles.indicator} />}
        </Button>
      </Tooltip>
    </li>
  )
})
